import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
    withStyles,
    Typography,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
} from '@material-ui/core';
import { CloudDownload } from '@material-ui/icons';
import { hasPermission } from '../../../sc-utilities';
import { snackActions } from '../../../sc-utilities/SnackBar';
import ProductsList from './ProductsList';
import { NavLink } from 'react-router-dom';
import { getErasureStatuses, getVerificationStatuses } from '../../../sc-product/product.utilities';
import * as actions from '../store/searchAction';

const styles = (theme) => ({
    '@global': {
        "[class*='productButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.buttonText.default,
            margin: theme.spacing(),
            padding: '10px 30px',
            textDecoration: 'none',
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
            borderRadius: '5px',
        },
    },
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingTop: theme.spacing(),
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%',
        },
    },
    productButtons: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.buttonText.default,
        margin: theme.spacing(),
        padding: '10px 30px',
        textDecoration: 'none',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        borderRadius: '5px',
    },
});

const Products = (props) => {
    const {
        classes,
        activeExternalServiceAvailable,
        translations,
        permissions,
        columns,
        tableRef,
        fetchTableData,
        tableOptions,
        activeCustomerID,
        onFetchProductReports,
        onDownloadProductReport,
    } = props;

    let visible = columns && columns['Products'];

    // Which row's reports are being looked up, and the picker for a device with several.
    const [loadingProductID, setLoadingProductID] = useState(null);
    const [reportPicker, setReportPicker] = useState({ open: false, reports: [] });

    const canDownloadReport = hasPermission(permissions, 'erasure_report_download');

    const downloadReport = async (reportID) => {
        setReportPicker({ open: false, reports: [] });
        await onDownloadProductReport({ ReportID: reportID, ActiveCustomerID: activeCustomerID });
    };

    /**
     * Resolves the device's generated reports and downloads the only one, or offers a choice.
     * The search hit carries the ProductID, which is the device identifier the report
     * endpoints require from a caller restricted to a single device.
     */
    const handleReportDownload = async (product) => {
        setLoadingProductID(product.ProductID);
        const reports = await onFetchProductReports({
            ProductID: product.ProductID,
            ActiveCustomerID: activeCustomerID,
        });
        setLoadingProductID(null);

        if (!reports.length) {
            return snackActions.snackBarComponent(
                translations['no_report_for_device'] || `There is no report for this device.`,
                'info'
            );
        }
        if (reports.length === 1) {
            return downloadReport(reports[0].ErasureReportID);
        }
        return setReportPicker({ open: true, reports });
    };

    const reportLabel = (report) => {
        const type = translations[report.ErasureReportType] || report.ErasureReportType;
        return report.OriginalFileName ? `${report.OriginalFileName} (${type})` : type;
    };

    const goToProduct = (productID, route, type) => {
        return `/${route}/details?productID=${productID}&type=${type}`;
    };

    const createProductAction = (product, paletteType) => {
        let path;
        switch (paletteType) {
            case 'PCProduct':
                path = hasPermission(permissions, 'product')
                    ? goToProduct(product[`ProductID`], 'pc-product', paletteType)
                    : null;
                break;
            case 'MobileProduct':
                path = hasPermission(permissions, 'product')
                    ? goToProduct(product[`ProductID`], 'mobile-product', paletteType)
                    : null;
                break;
            case 'StorageProduct':
                path = hasPermission(permissions, 'product')
                    ? goToProduct(product[`ProductID`], 'storage-product', paletteType)
                    : null;
                break;
            case 'WatchProduct':
                path = hasPermission(permissions, 'product')
                    ? goToProduct(product[`ProductID`], 'watch-product', paletteType)
                    : null;
                break;
            case 'HeadphoneProduct':
                path = hasPermission(permissions, 'product')
                    ? goToProduct(product[`ProductID`], 'headphone-product', paletteType)
                    : null;
                break;
            default:
                path = '';
        }

        if (!product[`InventoryID`]) {
            return translations['short_not_assigned'] || 'n/a';
        }

        // Without the `product` permission there is no detail page to link to, so render the
        // ID as plain text instead of a link that would navigate to the root.
        if (!path) {
            return <Typography variant={'caption'}>{product['InventoryID']}</Typography>;
        }

        return (
            <NavLink to={path} className={classes.productButtons}>
                {product['InventoryID']}
            </NavLink>
        );
    };

    let tableColumns = [
        {
            title: translations['action'] || 'Action',
            field: 'Action',
            hidden: !canDownloadReport,
            sorting: false,
            filtering: false,
            cellStyle: { minWidth: 70, maxWidth: 70 },
            render: (rowData) => {
                if (!canDownloadReport) {
                    return null;
                }
                if (loadingProductID === rowData.ProductID) {
                    return <CircularProgress size={20} />;
                }
                return (
                    <Tooltip title={translations['download_report'] || 'Download report(s)'}>
                        <IconButton
                            component={'div'}
                            aria-label={translations['download_report'] || 'Download report(s)'}
                            onClick={() => handleReportDownload(rowData)}
                        >
                            <CloudDownload />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            title: translations['inventory_id'] || 'Securaze ID',
            field: 'InventoryID',
            hidden: visible ? visible['InventoryID'] : false,
            filtering: false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            render: (rowData) => {
                return createProductAction(rowData, rowData.Type);
            },
        },
        {
            title: translations['type'] || 'Type',
            field: 'Type',
            hidden: visible ? visible['Type'] : false,
            filtering: false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.Type ? rowData.Type : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['serial_number'] || 'Serial number',
            field: 'SerialNumber',
            hidden: visible ? visible['SerialNumber'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.SerialNumber ? rowData.SerialNumber : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['imei'] || 'IMEI',
            field: 'IMEI',
            hidden: visible ? visible['IMEI'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.IMEI ? rowData.IMEI : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['imei_2'] || 'IMEI 2',
            field: 'IMEI2',
            hidden: visible ? visible['IMEI2'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.IMEI2 ? rowData.IMEI2 : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['model'] || 'Model',
            field: 'Model',
            hidden: visible ? visible['Model'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.Model ? rowData.Model : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['vendor'] || 'Vendor',
            field: 'Vendor',
            hidden: visible ? visible['Vendor'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.Vendor ? rowData.Vendor : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['external_id'] || 'ExternalID',
            field: 'ExternalID',
            hidden: visible ? visible['ExternalID'] : false,
            cellStyle: { minWidth: 220, maxWidth: 220 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.ExternalID ? rowData.ExternalID : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['external_service_type'] || 'External service type',
            field: 'ExternalServiceType',
            hidden: visible ? visible['ExternalServiceType'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },

            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.ExternalServiceType
                            ? rowData.ExternalServiceType
                            : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['diagnose'] || 'Diagnose',
            field: 'DiagnoseStatus',
            hidden: visible ? visible['DiagnoseStatus'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'} style={{ color: rowData?.DiagnoseStatus?.C || 'inherit' }}>
                        {rowData?.DiagnoseStatus?.T
                            ? rowData.DiagnoseStatus.T
                            : translations['short_not_assigned'] || 'n/a'}
                    </Typography>
                );
            },
        },
        {
            title: translations['erasure_status'] || 'Erasure status',
            field: 'ErasureStatus',
            hidden: visible ? visible['ErasureStatus'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: (rowData) => {
                return getErasureStatuses(rowData, null, classes, translations);
            },
        },
        {
            title: translations['verification_status'] || 'Verification status',
            field: 'VerificationStatus',
            hidden: visible ? visible['VerificationStatus'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: (rowData) => {
                return getVerificationStatuses(rowData, null, classes, translations);
            },
        },
        {
            title: translations['deleted'] || 'Deleted',
            field: 'Deleted',
            hidden: visible ? visible['Deleted'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: (rowData) => {
                return (
                    <Typography variant={'caption'}>
                        {rowData.Deleted ? translations['yes'] || 'Yes' : translations['no'] || 'No'}
                    </Typography>
                );
            },
        },
    ];

    if (!activeExternalServiceAvailable) {
        // Matched by field rather than by position: the column order is not fixed.
        tableColumns = tableColumns.filter(
            (column) => !['ExternalID', 'ExternalServiceType'].includes(column.field)
        );
    }

    // The rows come from /search/all, which is gated on global_search - not from the product
    // list endpoint. Roles that may search but not list products (EndUser, Restricted) must
    // still see their results.
    if (!hasPermission(permissions, 'global_search') && !hasPermission(permissions, 'product_list')) {
        return null;
    }

    return (
        <div className={classes.root}>
            <ProductsList
                tableColumns={tableColumns}
                tableRef={tableRef}
                fetchTableData={fetchTableData}
                tableOptions={tableOptions}
            />
            <Dialog
                open={reportPicker.open}
                onClose={() => setReportPicker({ open: false, reports: [] })}
                fullWidth
                maxWidth={'xs'}
            >
                <DialogTitle>{translations['download_report'] || 'Download report(s)'}</DialogTitle>
                <DialogContent>
                    <List>
                        {reportPicker.reports.map((report) => (
                            <ListItem
                                button
                                key={report.ErasureReportID}
                                onClick={() => downloadReport(report.ErasureReportID)}
                            >
                                <ListItemText primary={reportLabel(report)} />
                                <CloudDownload />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        translations:
            state.translate && state.translate.translations
                ? state.translate.translations.activeTranslations || {}
                : {},
        permissions: state?.core?.login?.permissions || [],
        activeExternalServiceAvailable: state.core.login.activeExternalServiceAvailable,
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onFetchProductReports: (params) => dispatch(actions.fetchProductReports(params)),
        onDownloadProductReport: (params) => dispatch(actions.downloadProductReport(params)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Products));
